/**
 * useNotifications Hook
 *
 * Fetches and manages in-app notifications for the current user.
 * Supports filtering, marking as read, and batch operations.
 *
 * @module useNotifications
 * @created 2025-12-19 - TASK-030
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  writeBatch,
  type QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { Notification, NotificationFilter } from '@/types/notification';

// ========================================
// Types
// ========================================

export interface UseNotificationsOptions {
  /** Maximum notifications to fetch */
  maxCount?: number;
  /** Initial filter settings */
  initialFilter?: NotificationFilter;
  /** Enable real-time updates (future) */
  realtime?: boolean;
}

export interface UseNotificationsReturn {
  /** Notification list */
  notifications: Notification[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Unread count */
  unreadCount: number;
  /** Current filter */
  filter: NotificationFilter;
  /** Set filter */
  setFilter: (filter: NotificationFilter) => void;
  /** Mark single notification as read */
  markAsRead: (notificationId: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Refresh notifications */
  refetch: () => Promise<void>;
  /** Is marking as read */
  isMarkingRead: boolean;
}

// ========================================
// Query Keys
// ========================================

const NOTIFICATIONS_QUERY_KEY = 'notifications';

// ========================================
// Fetch Function
// ========================================

async function fetchNotifications(
  userId: string,
  filter: NotificationFilter,
  maxCount: number
): Promise<Notification[]> {
  const constraints: QueryConstraint[] = [
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(maxCount),
  ];

  // Apply type filter
  if (filter.type && filter.type !== 'all') {
    constraints.push(where('type', '==', filter.type));
  }

  // Apply read filter
  if (filter.read !== undefined && filter.read !== 'all') {
    constraints.push(where('read', '==', filter.read));
  }

  // Apply family filter
  if (filter.familyId) {
    constraints.push(where('familyId', '==', filter.familyId));
  }

  const q = query(collection(db, 'notifications'), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Notification[];
}

// ========================================
// Hook
// ========================================

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const { maxCount = 50, initialFilter = { type: 'all', read: 'all' } } = options;

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<NotificationFilter>(initialFilter);

  // Fetch notifications
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, user?.uid, filter],
    queryFn: () => fetchNotifications(user!.uid, filter, maxCount),
    enabled: !!user?.uid,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  // Mark single as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { read: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user?.uid) return;

      const unreadNotifications = notifications.filter((n) => !n.read);
      if (unreadNotifications.length === 0) return;

      const batch = writeBatch(db);
      unreadNotifications.forEach((notification) => {
        const notificationRef = doc(db, 'notifications', notification.id);
        batch.update(notificationRef, { read: true });
      });

      await batch.commit();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
  });

  // Mark as read handler
  const markAsRead = useCallback(
    async (notificationId: string) => {
      await markAsReadMutation.mutateAsync(notificationId);
    },
    [markAsReadMutation]
  );

  // Mark all as read handler
  const markAllAsRead = useCallback(async () => {
    await markAllAsReadMutation.mutateAsync();
  }, [markAllAsReadMutation]);

  // Refetch handler
  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    notifications,
    isLoading,
    error: error as Error | null,
    unreadCount,
    filter,
    setFilter,
    markAsRead,
    markAllAsRead,
    refetch: handleRefetch,
    isMarkingRead: markAsReadMutation.isPending || markAllAsReadMutation.isPending,
  };
}

// ========================================
// Unread Count Only Hook
// ========================================

/**
 * Lightweight hook to just get unread notification count
 */
export function useUnreadNotificationCount(): {
  count: number;
  isLoading: boolean;
} {
  const { user } = useAuth();

  const { data: count = 0, isLoading } = useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'unread-count', user?.uid],
    queryFn: async () => {
      if (!user?.uid) return 0;

      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', user.uid),
        where('read', '==', false),
        limit(100)
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    },
    enabled: !!user?.uid,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  return { count, isLoading };
}
